import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import PhoneMock from "@/components/PhoneMock";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Droplet, Package, Star, Send, UploadCloud, Search, Wrench, UserMinus } from "lucide-react";

function OilChangeTab() {
  const [declines, setDeclines] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/declines")
      .then((r) => setDeclines(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return declines.filter((d) => {
      if ((d.type || "decline") !== "decline") return false;
      if (!q) return true;
      return (
        (d.customer_name || "").toLowerCase().includes(q) ||
        (d.vehicle || "").toLowerCase().includes(q) ||
        (d.customer_phone || "").includes(q)
      );
    });
  }, [declines, query]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.id)));
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one customer");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/oil-change/send", {
        decline_ids: Array.from(selected),
      });
      toast.success(`Free Oil Change sent to ${data.sent} · skipped ${data.skipped}`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Card className="p-5 border-border mb-5 bg-sky-50 dark:bg-sky-950/20 border-sky-200 dark:border-sky-900">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-sky-600 grid place-items-center shrink-0">
            <Droplet className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold">Free Oil Change Blast</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Pick a chunk of customers from your list and fire a FREE oil change offer with a unique coupon code.
              Great for filling slow days, winning back cold customers, or a lube-express promo.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, phone, or vehicle…"
            className="pl-9"
            data-testid="oil-search-input"
          />
        </div>
        <div className="font-mono-label text-muted-foreground">
          {selected.size} selected · {filtered.length} shown
        </div>
        <div className="flex-1" />
        <Button variant="outline" onClick={toggleAll} data-testid="oil-toggle-all-btn">
          {selected.size === filtered.length ? "Clear" : "Select all"}
        </Button>
        <Button onClick={send} disabled={busy || selected.size === 0} data-testid="oil-send-btn">
          <Send className="w-4 h-4 mr-2" strokeWidth={2} />
          Fire Free Oil Change ({selected.size})
        </Button>
      </div>

      <Card className="p-0 overflow-hidden border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Last Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-sm text-muted-foreground">
                  No customers to blast. Import declines from the dashboard first.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((d) => (
              <TableRow key={d.id} className="hover:bg-muted/40" data-testid={`oil-row-${d.id}`}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                    className="w-4 h-4"
                    data-testid={`oil-check-${d.id}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{d.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{d.customer_phone}</div>
                </TableCell>
                <TableCell className="text-sm">{d.vehicle}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">{d.status}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function PartsArrivedTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/parts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Parts Arrived — texted ${data.sent} customer${data.sent === 1 ? "" : "s"}`);
      setFile(null);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Card className="p-5 border-border mb-5 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-amber-600 grid place-items-center shrink-0">
            <Package className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold">Parts Arrived Notification</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Upload a CSV of customers whose ordered parts just landed. Every row auto-fires a personalized text
              from their assigned service consultant.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-border max-w-2xl">
        <div className="font-mono-label text-muted-foreground mb-1">CSV Upload</div>
        <div className="font-display text-lg font-bold tracking-tight mb-3">Fire Parts Arrived Texts</div>
        <div className="text-sm text-muted-foreground mb-4">
          Expected columns (any order, case-insensitive):{" "}
          <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">customer_name</code>,{" "}
          <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">customer_phone</code>,{" "}
          <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">vehicle</code>,{" "}
          <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">service_items</code> (part name),{" "}
          <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">advisor_name</code>.
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="parts-file" className="font-mono-label">Parts Arrived CSV</Label>
            <Input
              id="parts-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1.5"
              data-testid="parts-file-input"
            />
          </div>
          <Button onClick={upload} disabled={busy || !file} data-testid="parts-upload-btn">
            <UploadCloud className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Upload and Fire Texts
          </Button>
          {result && (
            <div className="rounded-md bg-muted/60 border border-border p-3 mt-2 text-sm">
              <div className="font-mono-label text-muted-foreground mb-1">Last import</div>
              <div>
                Texted <span className="font-bold text-emerald-600">{result.sent}</span> customer
                {result.sent === 1 ? "" : "s"} · skipped {result.skipped}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function ProspectTab() {
  const [declines, setDeclines] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bucket, setBucket] = useState("60");
  const [discount, setDiscount] = useState(15);
  const [busy, setBusy] = useState(false);
  const [offerKey, setOfferKey] = useState("winback");  // winback | oil_50 | tire_rotation | wipers_50 | tire_special | detail_50 | referral | final_chance
  const [couponCode, setCouponCode] = useState("DXOOUESS");

  const OFFERS = [
    { key: "winback", label: "$ Off Oil (default win-back)", accent: "bg-purple-600 hover:bg-purple-700", pillOff: "border-purple-300 text-purple-700" },
    { key: "oil_50", label: "50% OFF Oil Change", accent: "bg-teal-600 hover:bg-teal-700", pillOff: "border-teal-300 text-teal-700" },
    { key: "tire_rotation", label: "FREE Tire Rotation", accent: "bg-teal-700 hover:bg-teal-800", pillOff: "border-teal-300 text-teal-800" },
    { key: "wipers_50", label: "50% OFF Wiper Blades", accent: "bg-indigo-600 hover:bg-indigo-700", pillOff: "border-indigo-300 text-indigo-700" },
    { key: "tire_special", label: "Tire Special (Code: TIRES ARE TERRIFIC)", accent: "bg-fuchsia-600 hover:bg-fuchsia-700", pillOff: "border-fuchsia-300 text-fuchsia-700" },
    { key: "detail_50", label: "50% OFF Detail (thru Thu)", accent: "bg-pink-600 hover:bg-pink-700", pillOff: "border-pink-300 text-pink-700" },
    { key: "referral", label: "Send-a-Friend, Get FREE Oil", accent: "bg-lime-700 hover:bg-lime-800", pillOff: "border-lime-400 text-lime-800" },
    { key: "test_drive_1000", label: "$1,000 Test Drive & Buy (2027 Inventory)", accent: "bg-[hsl(var(--gold))] text-neutral-900 hover:opacity-90", pillOff: "border-[hsl(var(--gold))] text-[hsl(var(--gold))]" },
    { key: "new_car_detail", label: "$100 Off Full Detail (New-Car Owners)", accent: "bg-slate-700 hover:bg-slate-800", pillOff: "border-slate-400 text-slate-700" },
    { key: "ppf_wrap_45", label: "45% Off PPF Clear Wrap (thru 28th)", accent: "bg-cyan-700 hover:bg-cyan-800", pillOff: "border-cyan-400 text-cyan-700" },
    { key: "final_chance", label: "FINAL CHANCE 50% (cold customers)", accent: "bg-red-700 hover:bg-red-800", pillOff: "border-red-300 text-red-700" },
  ];

  // Live preview of what each offer's SMS actually looks like on a customer's phone.
  // Use this on the iPad during a pitch — tap any pill, phone bubble updates instantly.
  const previewFirstName = "Alicia";
  const previewDealership = "Precision Auto Group";
  const previewVehicle = "2021 Honda CR-V";
  const PREVIEWS = {
    winback: `Hi ${previewFirstName}, we haven't seen you in a while at ${previewDealership}. Please accept $15 OFF your next oil change on your ${previewVehicle} — use code ${couponCode}. Book here: drvlgc.co/s/8K3  |  Call us: drvlgc.co/c/8K3`,
    oil_50: `Hi ${previewFirstName}, this is ${previewDealership}. 50% OFF your next oil change on your ${previewVehicle} — use code ${couponCode}. Schedule here: drvlgc.co/s/8K3`,
    tire_rotation: `Hi ${previewFirstName}, this is ${previewDealership}. FREE tire rotation on your ${previewVehicle} — use code ${couponCode}. Schedule here: drvlgc.co/s/8K3`,
    wipers_50: `Hi ${previewFirstName}, this is ${previewDealership}. 50% OFF wiper blades on your ${previewVehicle} — use code ${couponCode}. Book now: drvlgc.co/s/8K3`,
    tire_special: `Hi ${previewFirstName}, this is ${previewDealership}. Tire special this week only — mention code 'TIRES ARE TERRIFIC' at check-in for your discount. Schedule: drvlgc.co/s/8K3`,
    detail_50: `Hi ${previewFirstName}, this is ${previewDealership}. 50% OFF detail service on your ${previewVehicle} thru next Thursday — use code ${couponCode}. Schedule: drvlgc.co/s/8K3`,
    referral: `Hi ${previewFirstName}, this is ${previewDealership}. Send a friend or neighbor in and your next oil service is FREE. Code ${couponCode}. Book them: drvlgc.co/s/8K3`,
    test_drive_1000: `Hi ${previewFirstName}, this is ${previewDealership}. We'll PAY YOU $1,000 to test drive AND purchase any 2027 vehicle in stock this week. Code ${couponCode}. Details & schedule: drvlgc.co/s/8K3`,
    new_car_detail: `Hi ${previewFirstName}, this is ${previewDealership}. New-car owner special: $100 OFF a Full Detail on your ${previewVehicle}. Use code ${couponCode}. Schedule here: drvlgc.co/s/8K3`,
    ppf_wrap_45: `Hi ${previewFirstName}, this is ${previewDealership}. Protect your ${previewVehicle} — 45% OFF PPF Clear Wrap thru the 28th. Use code ${couponCode}. Schedule here: drvlgc.co/s/8K3`,
    final_chance: `Hi ${previewFirstName}, this is ${previewDealership}. FINAL CHANCE — 50% OFF Brake pads, Cabin filter on your ${previewVehicle}. Use code DD-FINAL-A7B2K9. Schedule: drvlgc.co/s/8K3  |  Call: drvlgc.co/c/8K3`,
  };

  const load = () =>
    Promise.all([api.get("/declines"), api.get("/settings")])
      .then(([r, s]) => {
        setDeclines(r.data);
        setDiscount(s.data?.prospect_discount ?? 15);
        setCouponCode(s.data?.fast_services_coupon_code ?? "DXOOUESS");
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const now = Date.now();
  const ageDays = (d) => (now - new Date(d.decline_date).getTime()) / 86400000;
  const buckets = { "30": [30, 59], "60": [60, 89], "90": [90, 99999] };
  const [lo, hi] = buckets[bucket];
  const filtered = useMemo(
    () => declines.filter((d) => (d.type || "decline") === "decline" && ageDays(d) >= lo && ageDays(d) <= hi),
    [declines, bucket]
  );

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const send = async () => {
    if (selected.size === 0) { toast.error("Pick at least one customer"); return; }
    setBusy(true);
    try {
      if (offerKey === "winback") {
        const { data } = await api.post("/campaigns/prospect/send", { decline_ids: Array.from(selected) });
        toast.success(`Win-back sent to ${data.sent} · skipped ${data.skipped}`);
      } else {
        const { data } = await api.post("/campaigns/fast-service/send", { decline_ids: Array.from(selected), offer_key: offerKey });
        toast.success(`${OFFERS.find((o) => o.key === offerKey).label} sent to ${data.sent} · skipped ${data.skipped}`);
      }
      setSelected(new Set());
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const activeOffer = OFFERS.find((o) => o.key === offerKey);

  return (
    <div>
      <Card className="p-5 border-border mb-5 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-purple-600 grid place-items-center shrink-0">
            <UserMinus className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold">Fast Services · Drive Traffic</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Pick an offer, pick a customer bucket (30 / 60 / 90+ days since last decline), check the customers
              you want to blast, and fire. Fast Services offers share the coupon code{" "}
              <b className="font-mono">{couponCode}</b> (change in Settings).
            </p>
          </div>
        </div>
      </Card>

      {/* Live SMS preview — colorful iPhone mock, styled per selected offer */}
      <div className="mb-6 flex flex-col items-center gap-2 no-print" data-testid="offer-preview">
        <PhoneMock
          dealer={previewDealership}
          from="+1 (214) 555-0180 · Dealership"
          body={PREVIEWS[offerKey]}
          sent="9:04 AM"
          label={activeOffer?.label}
          accent={activeOffer?.accent || "bg-purple-600 text-white"}
          scheduleUrl="https://drvlgc.co/s/8K3"
          callUrl="https://drvlgc.co/c/8K3"
          testid={`offer-preview-${offerKey}`}
        />
        <div className="font-mono-label text-muted-foreground text-[0.6rem] tracking-widest">
          Live preview · tap any offer below to update
        </div>
      </div>

      {/* Offer type picker */}
      <div className="mb-4">
        <div className="font-mono-label text-muted-foreground mb-2">Pick an offer</div>
        <div className="flex flex-wrap gap-2" data-testid="offer-picker">
          {OFFERS.map((o) => (
            <button
              key={o.key}
              onClick={() => { setOfferKey(o.key); setSelected(new Set()); }}
              data-testid={`offer-pill-${o.key}`}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                offerKey === o.key ? `${o.accent} text-white border-transparent` : `bg-white dark:bg-transparent ${o.pillOff} hover:bg-muted`
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {["30", "60", "90"].map((b) => (
          <button
            key={b}
            onClick={() => { setBucket(b); setSelected(new Set()); }}
            data-testid={`prospect-bucket-${b}`}
            className={`px-4 py-2 rounded-md border font-mono-label text-xs transition-colors ${
              bucket === b ? "border-purple-600 bg-purple-600 text-white" : "border-border hover:bg-muted"
            }`}
          >
            {b === "90" ? "90+ Days" : `${b}-${Number(b) + 29} Days`}
          </button>
        ))}
        <div className="font-mono-label text-muted-foreground ml-2">{selected.size} selected · {filtered.length} in bucket</div>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((d) => d.id)))} data-testid="prospect-toggle-all">
          {selected.size === filtered.length ? "Clear" : "Select all"}
        </Button>
        <Button onClick={send} disabled={busy || selected.size === 0} data-testid="prospect-send-btn" className={activeOffer?.accent}>
          <Send className="w-4 h-4 mr-2" strokeWidth={2} />
          Fire {activeOffer?.label.split("(")[0].trim()} ({selected.size})
        </Button>
      </div>

      <Card className="p-0 overflow-hidden border-border">
        <Table>
          <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Customer</TableHead><TableHead>Vehicle</TableHead><TableHead>Days Since Decline</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-sm text-muted-foreground">No customers in this bucket.</TableCell></TableRow>
            )}
            {filtered.map((d) => (
              <TableRow key={d.id} className="hover:bg-muted/40" data-testid={`prospect-row-${d.id}`}>
                <TableCell><input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} className="w-4 h-4" data-testid={`prospect-check-${d.id}`} /></TableCell>
                <TableCell><div className="font-medium">{d.customer_name}</div><div className="text-xs text-muted-foreground">{d.customer_phone}</div></TableCell>
                <TableCell className="text-sm">{d.vehicle}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{Math.floor(ageDays(d))} days</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function BodyShopTab() {
  const [declines, setDeclines] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [discount, setDiscount] = useState(10);

  const load = () =>
    Promise.all([api.get("/declines"), api.get("/settings")])
      .then(([r, s]) => {
        setDeclines(r.data);
        setDiscount(s.data?.body_shop_discount ?? 10);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return declines.filter((d) => {
      if ((d.type || "decline") !== "decline") return false;
      if (!q) return true;
      return (
        (d.customer_name || "").toLowerCase().includes(q) ||
        (d.vehicle || "").toLowerCase().includes(q) ||
        (d.customer_phone || "").includes(q)
      );
    });
  }, [declines, query]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.id)));
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one customer");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/body-shop/send", {
        decline_ids: Array.from(selected),
      });
      toast.success(`Body Shop offer sent to ${data.sent} · skipped ${data.skipped}`);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Card className="p-5 border-border mb-5 bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-rose-600 grid place-items-center shrink-0">
            <Wrench className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold">Body Shop Estimate Reminder</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Send a <b>{discount}% OFF</b> collision / paint offer to any customer list. Great for
              re-engaging customers with a scratch, dent, or fender damage on file — every text includes a
              unique coupon and a link to book a free estimate. Change the % in Settings anytime.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, phone, or vehicle…"
            className="pl-9"
            data-testid="body-search-input"
          />
        </div>
        <div className="font-mono-label text-muted-foreground">
          {selected.size} selected · {filtered.length} shown
        </div>
        <div className="flex-1" />
        <Button variant="outline" onClick={toggleAll} data-testid="body-toggle-all-btn">
          {selected.size === filtered.length ? "Clear" : "Select all"}
        </Button>
        <Button onClick={send} disabled={busy || selected.size === 0} data-testid="body-send-btn">
          <Send className="w-4 h-4 mr-2" strokeWidth={2} />
          Fire {discount}% Body Shop Offer ({selected.size})
        </Button>
      </div>

      <Card className="p-0 overflow-hidden border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Last Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-sm text-muted-foreground">
                  No customers to blast. Import declines from the dashboard first.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((d) => (
              <TableRow key={d.id} className="hover:bg-muted/40" data-testid={`body-row-${d.id}`}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                    className="w-4 h-4"
                    data-testid={`body-check-${d.id}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{d.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{d.customer_phone}</div>
                </TableCell>
                <TableCell className="text-sm">{d.vehicle}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">{d.status}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function CsiTab() {
  const [appts, setAppts] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api
      .get("/appointments")
      .then((r) => setAppts(r.data.filter((a) => a.status === "closed")))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const send = async (appt) => {
    setBusyId(appt.id);
    try {
      await api.post(`/appointments/${appt.id}/send-csi`);
      toast.success(`CSI text queued for ${appt.customer_name}`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <Card className="p-5 border-border mb-5 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-emerald-600 grid place-items-center shrink-0">
            <Star className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold">Customer Satisfaction (CSI) Follow-Up</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              After a repair order closes, the nightly engine automatically fires a CSI text ~24 hours later. Not
              happy customers are routed directly to your Service Manager. You can also send a CSI manually from
              this list.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden border-border">
        <div className="p-4 border-b border-border font-mono-label text-muted-foreground">
          Closed appointments — {appts.length}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Closed</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right">RO Total</TableHead>
              <TableHead>CSI Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                  No closed appointments yet. Close some ROs from Appointments (or "Boost Demo") to see them here.
                </TableCell>
              </TableRow>
            )}
            {appts.map((a) => (
              <TableRow key={a.id} className="hover:bg-muted/40" data-testid={`csi-row-${a.id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {a.closed_at ? new Date(a.closed_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{a.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{a.customer_phone}</div>
                </TableCell>
                <TableCell className="text-sm">{a.vehicle}</TableCell>
                <TableCell className="text-right tabular-nums">
                  ${Number(a.ro_total || 0).toLocaleString()}
                </TableCell>
                <TableCell>
                  {a.csi_sent_at ? (
                    <Badge className="bg-emerald-600 text-white">Sent</Badge>
                  ) : (
                    <Badge variant="outline">Pending</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant={a.csi_sent_at ? "outline" : "default"}
                    onClick={() => send(a)}
                    disabled={busyId === a.id}
                    data-testid={`csi-send-${a.id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                    {a.csi_sent_at ? "Resend" : "Send CSI"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default function CampaignsPage() {
  const location = useLocation();
  const [bodyShopEnabled, setBodyShopEnabled] = useState(false);
  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setBodyShopEnabled(Boolean(r.data?.body_shop_enabled)))
      .catch(() => {});
  }, []);

  // Read ?t=<tab> so Dashboard quick-launch cards can deep-link to the right blast
  const initialTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get("t");
    return ["oil", "parts", "prospect", "body", "csi"].includes(t) ? t : "prospect";
  }, [location.search]);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Send a Text Blast" />
      <div className="mb-6">
        <div className="font-mono-label text-muted-foreground">Text Blasts</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Send a Text Blast</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Pick which specialty text campaign to fire — win-back a prospect bucket (30/60/90 days),
          blast a free oil-change offer, notify parts have arrived, or follow up with a CSI text
          after service.
        </p>
      </div>

      {/* Quick-shortcut cards removed — they duplicated the tabs below and
          threw a runtime error on tap. The tabs alone are the entry point now. */}

      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="mb-5 flex flex-wrap gap-1.5 bg-transparent h-auto p-0">
          <TabsTrigger value="prospect" data-testid="tab-prospect" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white bg-purple-50 text-purple-800 border-2 border-purple-200 data-[state=active]:border-purple-700 rounded-full px-4 py-2 hover:bg-purple-100 transition">
            <UserMinus className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Fast Services · Drive Traffic
          </TabsTrigger>
          <TabsTrigger value="oil" data-testid="tab-oil" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white bg-sky-50 text-sky-800 border-2 border-sky-200 data-[state=active]:border-sky-700 rounded-full px-4 py-2 hover:bg-sky-100 transition">
            <Droplet className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Free Oil Change
          </TabsTrigger>
          <TabsTrigger value="parts" data-testid="tab-parts" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white bg-amber-50 text-amber-800 border-2 border-amber-200 data-[state=active]:border-amber-700 rounded-full px-4 py-2 hover:bg-amber-100 transition">
            <Package className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Parts Arrived
          </TabsTrigger>
          {bodyShopEnabled && (
            <TabsTrigger value="body" data-testid="tab-body" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white bg-rose-50 text-rose-800 border-2 border-rose-200 data-[state=active]:border-rose-700 rounded-full px-4 py-2 hover:bg-rose-100 transition">
              <Wrench className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Body Shop
            </TabsTrigger>
          )}
          <TabsTrigger value="csi" data-testid="tab-csi" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white bg-emerald-50 text-emerald-800 border-2 border-emerald-200 data-[state=active]:border-emerald-700 rounded-full px-4 py-2 hover:bg-emerald-100 transition">
            <Star className="w-4 h-4 mr-2" strokeWidth={1.75} />
            CSI Follow-Up
          </TabsTrigger>
        </TabsList>
        <TabsContent value="prospect">
          <ProspectTab />
        </TabsContent>
        <TabsContent value="oil">
          <OilChangeTab />
        </TabsContent>
        <TabsContent value="parts">
          <PartsArrivedTab />
        </TabsContent>
        {bodyShopEnabled && (
          <TabsContent value="body">
            <BodyShopTab />
          </TabsContent>
        )}
        <TabsContent value="csi">
          <CsiTab />
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
